import React, { useState, useRef, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import EmojiPicker from 'emoji-picker-react'
import { ReactComponent as FileUploadIcon } from 'assets/svgs/file-upload-icon.svg'
import { ReactComponent as MentionIcon } from 'assets/svgs/mention-icon.svg'
import { ReactComponent as BoldIcon } from 'assets/svgs/bold-icon.svg'
import { ReactComponent as ItalicIcon } from 'assets/svgs/italic-icon.svg'
import { ReactComponent as StrikethroughIcon } from 'assets/svgs/strikethrought-icon.svg'
import { ReactComponent as ListBulletIcon } from 'assets/svgs/list-bullet-icon.svg'
import { ReactComponent as HyperlinkIcon } from 'assets/svgs/hyperlink-icon.svg'

interface MeetingDescriptionEditorProps {
  value: string
  onChange: (content: string) => void
  placeholder?: string
  onAttachClick?: () => void
  onMentionClick?: () => void
}

export const MeetingDescriptionEditor: React.FC<MeetingDescriptionEditorProps> = ({
  value,
  onChange,
  placeholder = 'Add Description',
  onAttachClick,
  onMentionClick,
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const emojiButtonRef = useRef<HTMLButtonElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        blockquote: false,
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'text-app-primary-60 underline hover:text-app-primary-70 cursor-pointer',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      onChange(html)
      // Check if editor is empty
      setIsEmpty(editor.isEmpty)
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
      },
    },
  })

  // Check initial empty state
  useEffect(() => {
    if (editor) {
      setIsEmpty(editor.isEmpty)
    }
  }, [editor])

  if (!editor) {
    return null
  }

  const handleEmojiClick = (emojiData: any) => {
    editor.chain().focus().insertContent(emojiData.emoji).run()
    setShowEmojiPicker(false)
  }

  const handleAddLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run()
      setLinkUrl('')
      setShowLinkDialog(false)
    }
  }

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run()
    setShowLinkDialog(false)
  }

  return (
    <div className="border border-app-primary-30 rounded-xl bg-white focus-within:border-app-primary-60 transition-colors">
      {/* Editor with placeholder overlay */}
      <div className="meeting-description-editor relative">
        {isEmpty && (
          <div className="absolute top-[12px] left-[16px] text-[#9ca3af] pointer-events-none">
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="px-3 py-2 bg-app-grey-5 border-t border-app-grey-25">
          <div className="flex items-center gap-2">
            <input
              ref={linkInputRef}
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-3 py-2 text-sm border border-app-primary-30 rounded-lg focus:outline-none focus:border-app-primary-60"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddLink()
                } else if (e.key === 'Escape') {
                  setShowLinkDialog(false)
                  setLinkUrl('')
                }
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={handleAddLink}
              className="px-4 py-2 text-sm bg-app-primary-60 text-white rounded-lg hover:bg-app-primary-70"
            >
              Add
            </button>
            {editor.isActive('link') && (
              <button
                type="button"
                onClick={handleRemoveLink}
                className="px-4 py-2 text-sm border border-app-primary-30 text-app-grey-80 rounded-lg hover:bg-app-grey-20"
              >
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setShowLinkDialog(false)
                setLinkUrl('')
              }}
              className="px-4 py-2 text-sm border border-app-primary-30 text-app-grey-80 rounded-lg hover:bg-app-grey-20"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Toolbar at bottom */}
      <div className="flex items-center justify-between px-2 py-2 border-t border-app-grey-25">
        <div className="flex items-center gap-1">
          {/* Attach */}
          <button
            type="button"
            onClick={onAttachClick}
            className="p-2 rounded hover:bg-app-grey-20 transition-colors"
            title="Attach file"
          >
            <FileUploadIcon className="w-5 h-5" />
          </button>

          {/* Emoji */}
          <div className="relative">
            <button
              ref={emojiButtonRef}
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 rounded hover:bg-app-grey-20 transition-colors"
              title="Insert Emoji"
            >
              <span className="text-lg">🙂</span>
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-full mb-2 left-0 z-50">
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </div>
            )}
          </div>

          {/* Mention */}
          <button
            type="button"
            onClick={onMentionClick}
            className="p-2 rounded hover:bg-app-grey-20 transition-colors"
            title="Mention User"
          >
            <MentionIcon className="w-5 h-5" />
          </button>

          {/* Link */}
          <button
            type="button"
            onClick={() => setShowLinkDialog(!showLinkDialog)}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('link') ? 'bg-app-grey-20' : ''
            }`}
            title="Insert Link"
          >
            <HyperlinkIcon className="w-5 h-5" />
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-app-grey-25 mx-1" />

          {/* Bold */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('bold') ? 'bg-app-grey-20' : ''
            }`}
            title="Bold"
          >
            <BoldIcon className="w-5 h-5" />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('italic') ? 'bg-app-grey-20' : ''
            }`}
            title="Italic"
          >
            <ItalicIcon className="w-5 h-5" />
          </button>

          {/* Strikethrough */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('strike') ? 'bg-app-grey-20' : ''
            }`}
            title="Strikethrough"
          >
            <StrikethroughIcon className="w-5 h-5" />
          </button>

          {/* Bullet List */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('bulletList') ? 'bg-app-grey-20' : ''
            }`}
            title="Bullet List"
          >
            <ListBulletIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Placeholder Styling */}
      <style>{`
        .meeting-description-editor .ProseMirror {
          padding: 12px 16px;
          min-height: 100px;
          outline: none;
        }
      `}</style>
    </div>
  )
}
